import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminQuickNav from '../../components/AdminQuickNav';
import { growthService, type GrowthReportRecord } from '../../services/growthService';
import {
  HiTrendingUp,
  HiLightningBolt,
  HiCheckCircle,
  HiRefresh,
  HiShieldCheck,
  HiSparkles,
  HiDocumentText,
  HiGlobe,
  HiSearch,
} from 'react-icons/hi';

const TARGET_KEYWORDS = [
  { keyword: 'استودیو بازی سازی ایرانی', intent: 'Entity / Authority', volume: 'High', priority: 'P1 - اولویت نخست' },
  { keyword: 'استودیو بازی سازی', intent: 'Commercial / Discovery', volume: 'High', priority: 'P1 - اولویت نخست' },
  { keyword: 'استودیو گیم سازی', intent: 'General Query', volume: 'Medium', priority: 'P2 - اولویت دوم' },
  { keyword: 'بازی ایرانی Stop Duck', intent: 'Branded / Download', volume: 'Medium', priority: 'P1 - اولویت نخست' },
  { keyword: 'بازی سازی با آنریل انجین 5', intent: 'Technical / Informational', volume: 'High', priority: 'P2 - اولویت دوم' },
  { keyword: 'Iranian Indie Game Studio', intent: 'International / Global', volume: 'Medium', priority: 'P1 - اولویت نخست' },
];

export default function GrowthIntelligenceAdminPage() {
  const [reports, setReports] = useState<GrowthReportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [runningAudit, setRunningAudit] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedReport, setSelectedReport] = useState<GrowthReportRecord | null>(null);
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await growthService.getLatestReports();
      setReports(data);
      if (data.length > 0) {
        setSelectedReport(data[0]);
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  const handleRunAudit = async () => {
    if (runningAudit) return;
    setRunningAudit(true);
    setStatusMsg(null);

    try {
      const job = await growthService.triggerGrowthAnalysis();
      setStatusMsg({
        type: 'success',
        text: 'درخواست ممیزی هوشمند به موتور ایجنت‌ها ارسال شد. در حال استخراج متریک‌ها و تحلیل با هوش مصنوعی...',
      });

      let polls = 0;
      pollTimerRef.current = setInterval(async () => {
        polls++;
        try {
          const status = await growthService.getJobStatus(job.id);
          if (status.status === 'completed') {
            clearInterval(pollTimerRef.current!);
            setRunningAudit(false);
            setStatusMsg({
              type: 'success',
              text: 'گزارش ممیزی رشد و سئو با موفقیت توسط هوش مصنوعی تولید و ثبت شد.',
            });
            await loadData();
          } else if (status.status === 'failed') {
            clearInterval(pollTimerRef.current!);
            setRunningAudit(false);
            setStatusMsg({
              type: 'error',
              text: `تحلیل با خطا مواجه شد: ${status.error_message || 'خطای سرور'}`,
            });
          } else if (polls >= 30) {
            clearInterval(pollTimerRef.current!);
            setRunningAudit(false);
            setStatusMsg({
              type: 'error',
              text: 'پاسخ از سرور در زمان مقرر دریافت نشد. مطمئن شوید سرور در ترمینال روشن است.',
            });
          }
        } catch (e) {
          console.error(e);
        }
      }, 3000);
    } catch (err: any) {
      setRunningAudit(false);
      setStatusMsg({ type: 'error', text: err.message || 'خطا در اجرای ممیزی.' });
    }
  };

  const latestDraft = selectedReport?.generated_draft;

  return (
    <div className="min-h-screen bg-black text-white pt-24 pb-16 px-4 md:px-8" dir="rtl">
      <Helmet>
        <title>مرکز هوشمند ممیزی رشد، SEO و GEO | پنل ادمین Active Legend</title>
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <AdminQuickNav />

        {/* Top Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/20 rounded-2xl text-primary border border-primary/30">
              <HiTrendingUp className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-white">
                سیستم هوشمند رشد ارگانیک، SEO و موتورهای هوش مصنوعی (GEO)
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                عامل مستقل تحلیل رشد، پایش رتبه در ChatGPT/Perplexity و تولید گزارش‌های روزانه اختصاصی
              </p>
            </div>
          </div>

          <button
            onClick={handleRunAudit}
            disabled={runningAudit}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-black font-extrabold px-6 py-3 rounded-2xl shadow-lg shadow-primary/20 transition-all hover:scale-105 active:scale-95 disabled:opacity-50 self-start md:self-auto text-sm"
          >
            {runningAudit ? (
              <>
                <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                <span>در حال تحلیل و ارزیابی...</span>
              </>
            ) : (
              <>
                <HiLightningBolt className="w-5 h-5" />
                <span>اجرای آنی ممیزی و تحلیل رشد</span>
              </>
            )}
          </button>
        </div>

        {/* Notification */}
        {statusMsg && (
          <div
            className={`p-4 rounded-2xl mb-6 text-sm font-medium border flex items-center justify-between ${
              statusMsg.type === 'success'
                ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-300'
                : 'bg-red-950/60 border-red-500/40 text-red-300'
            }`}
          >
            <span>{statusMsg.text}</span>
            <button onClick={() => setStatusMsg(null)} className="text-white/60 hover:text-white">
              ✕
            </button>
          </div>
        )}

        {/* Core KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-zinc-900/90 border border-emerald-500/30 rounded-3xl p-5 shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-emerald-400 font-bold">شاخص سئو تکنیکال (Technical SEO)</span>
              <HiShieldCheck className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="text-3xl font-black text-white">۹۵٪</div>
            <div className="text-[11px] text-gray-400 mt-2 flex items-center gap-1">
              <HiCheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <span>Breadcrumbs + Schema.org + ساختار H1/H2</span>
            </div>
          </div>

          <div className="bg-zinc-900/90 border border-primary/30 rounded-3xl p-5 shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-primary font-bold">شاخص استناد در هوش مصنوعی (GEO)</span>
              <HiSparkles className="w-5 h-5 text-primary" />
            </div>
            <div className="text-3xl font-black text-white">۹۸٪</div>
            <div className="text-[11px] text-gray-400 mt-2 flex items-center gap-1">
              <HiCheckCircle className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>پوشش کامل llms.txt + ربات‌های AI</span>
            </div>
          </div>

          <div className="bg-zinc-900/90 border border-blue-500/30 rounded-3xl p-5 shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-blue-400 font-bold">سازگاری با Google Discover</span>
              <HiGlobe className="w-5 h-5 text-blue-400" />
            </div>
            <div className="text-3xl font-black text-white">۱۰۰٪</div>
            <div className="text-[11px] text-gray-400 mt-2 flex items-center gap-1">
              <HiCheckCircle className="w-3.5 h-3.5 text-blue-400 shrink-0" />
              <span>تصاویر عریض + متا تگ max-image-preview</span>
            </div>
          </div>

          <div className="bg-zinc-900/90 border border-purple-500/30 rounded-3xl p-5 shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-purple-400 font-bold">آدرس‌های ثبت‌شده در sitemap.xml</span>
              <HiDocumentText className="w-5 h-5 text-purple-400" />
            </div>
            <div className="text-3xl font-black text-white">
              {latestDraft?.sitemap_urls_count || 15} URL
            </div>
            <div className="text-[11px] text-gray-400 mt-2 flex items-center gap-1">
              <HiCheckCircle className="w-3.5 h-3.5 text-purple-400 shrink-0" />
              <span>تولید خودکار در زمان هر بیلد</span>
            </div>
          </div>
        </div>

        {/* Strategic Keyword Matrix */}
        <div className="bg-zinc-900/90 border border-white/10 rounded-3xl p-6 md:p-8 mb-8 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg md:text-xl font-bold text-white flex items-center gap-2">
                <HiSearch className="w-5 h-5 text-primary" />
                ماتریس کلمات کلیدی هدف و جایگاه رقابتی در جستجو و هوش مصنوعی
              </h2>
              <p className="text-xs text-gray-400 mt-1">
                عبارات کلیدی کانونی که وبسایت برای کسب رتبه نخست در آن‌ها بهینه‌سازی شده است
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-right text-xs md:text-sm">
              <thead>
                <tr className="border-b border-white/10 text-gray-400">
                  <th className="pb-3 pr-2">کلمه کلیدی هدف</th>
                  <th className="pb-3 px-2">قصد جستجو (Search Intent)</th>
                  <th className="pb-3 px-2">پتانسیل ترافیک</th>
                  <th className="pb-3 pl-2">اولویت استراتژیک</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {TARGET_KEYWORDS.map((k, i) => (
                  <tr key={i} className="hover:bg-white/5 transition-colors">
                    <td className="py-3.5 pr-2 font-bold text-white font-mono">{k.keyword}</td>
                    <td className="py-3.5 px-2 text-gray-300">{k.intent}</td>
                    <td className="py-3.5 px-2 text-primary font-bold">{k.volume}</td>
                    <td className="py-3.5 pl-2">
                      <span className="text-[10px] bg-primary/10 text-primary border border-primary/30 px-2.5 py-1 rounded-full font-bold">
                        {k.priority}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Latest Private Daily AI Growth Report */}
        <div className="bg-zinc-900/90 border border-white/10 rounded-3xl p-6 md:p-8 mb-8 shadow-2xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-white/10">
            <div>
              <div className="flex items-center gap-2 text-primary font-bold text-xs mb-1">
                <HiDocumentText className="w-4 h-4" />
                <span>گزارش روزانه اختصاصی مدیر (Private Daily AI Growth & SEO Report)</span>
              </div>
              <h2 className="text-xl font-bold text-white">
                آخرین تحلیل تحلیلی رشد و استراتژی وبسایت
              </h2>
              {selectedReport && (
                <div className="text-xs text-gray-400 mt-1">
                  تاریخ تحلیل: {new Date(selectedReport.created_at).toLocaleDateString('fa-IR')} — ساعت{' '}
                  {new Date(selectedReport.created_at).toLocaleTimeString('fa-IR')}
                </div>
              )}
            </div>

            <button
              onClick={loadData}
              className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-xs text-gray-300 px-4 py-2 rounded-xl border border-white/10 transition-all self-start sm:self-auto"
            >
              <HiRefresh className="w-4 h-4 text-primary" />
              <span>بروزرسانی داده‌ها</span>
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-400 text-sm">در حال بارگذاری گزارش...</div>
          ) : selectedReport ? (
            <div className="space-y-6">
              {/* Quick Summary Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-black/50 border border-white/10 rounded-2xl p-4 text-center">
                  <div className="text-xs text-gray-400 mb-1">کل مقالات ایندکس‌شده</div>
                  <div className="text-xl font-black text-white">{latestDraft?.total_posts || 0}</div>
                </div>
                <div className="bg-black/50 border border-white/10 rounded-2xl p-4 text-center">
                  <div className="text-xs text-gray-400 mb-1">بازی‌های اختصاصی</div>
                  <div className="text-xl font-black text-white">{latestDraft?.total_games || 0}</div>
                </div>
                <div className="bg-black/50 border border-white/10 rounded-2xl p-4 text-center">
                  <div className="text-xs text-gray-400 mb-1">کل نظرات ثبت‌شده</div>
                  <div className="text-xl font-black text-white">{latestDraft?.total_comments || 0}</div>
                </div>
                <div className="bg-black/50 border border-white/10 rounded-2xl p-4 text-center">
                  <div className="text-xs text-gray-400 mb-1">امتیاز سلامت سئو و GEO</div>
                  <div className="text-xl font-black text-primary">
                    {selectedReport.score || 95} / ۱۰۰
                  </div>
                </div>
              </div>

              {/* Full Markdown Content Render */}
              <div className="bg-black/60 border border-white/10 rounded-2xl p-6 sm:p-8 text-sm text-gray-200 leading-relaxed font-normal whitespace-pre-wrap">
                {latestDraft?.report_markdown || 'متن گزارشی ثبت نشده است.'}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400 bg-black/30 rounded-2xl border border-white/5">
              هنوز گزارش روزانه‌ای ایجاد نشده است. روی دکمه «اجرای آنی ممیزی و تحلیل رشد» در بالای صفحه کلیک کنید.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
